import type { App } from 'vue';
import { useUserStore } from '@/store/modules/user/store.js';
import { useAccountStore } from '@/store/modules/account/store.js';
import LaravelEcho from 'laravel-echo';
import axios from '@/plugins/axios.js';
import createLegacyEmitterAdapter from '@/plugins/createLegacyEmitterAdapter.ts';
import { authService } from '@/plugins/vue-auth.ts';
import 'pusher-js'; // Side-effect import: required by laravel-echo

/**
 * Real-time bridge between backend broadcasts and frontend UI state.
 *
 * Uses Laravel Echo/Pusher to subscribe to:
 * - `global`: deploy/reload signals
 * - `account_<id>`: CRM entity updates (lead, communication, task event, visit, web-order badge, automation state)
 * - `user_<id>`: per-user notifications, call/screen-pop updates, export results, and user whispers
 *
 * Concrete backend -> frontend examples used by the app:
 * - `Lead.LeadUpdatedBroadcast` on `account_<id>`: reloads/updates lead data in UI lists and dashboards.
 * - `WebOrderBadgeCountUpdated` on `account_<id>`: updates the web-order badge counter.
 * - `Notification.NewNotification` on `user_<id>`: updates user notification state in real time.
 * - `Automation\\UpdateAffectedModels` on `user_<id>`: refreshes automation impacted-model counters.
 *
 * Also exposes legacy `$on/$off/$emit` so existing Vue 2-era listeners can react to
 * `global-channel-ready`, `account-channel-ready`, and `user-channel-ready` during migration.
 */
const echoConfig = {
    auth: {
        headers: {
            Authorization: null as string | null,
        },
    },
    broadcaster: 'pusher' as const,
    cluster: process.env.VUE_APP_PUSHER_CLUSTER,
    key: process.env.VUE_APP_PUSHER_KEY,
    channelAuthorization: {
        endpoint: `${process.env.VUE_APP_API_URL}/broadcasting/auth`,
        headers: {
            Authorization: null as string | null,
        },
    },
    ...(process.env.VUE_APP_POXA_KEY && {
        forceTLS: false,
        wsHost: process.env.VUE_APP_POXA_HOST,
        wsPort: process.env.VUE_APP_POXA_PORT,
        enabledTransports: ['ws'],
        key: process.env.VUE_APP_POXA_KEY,
    }),
};

const echo = new LaravelEcho(echoConfig);

export const broadcasting = {
    ...createLegacyEmitterAdapter(),
    echo,
    channels: {
        account: null as { name: string } | null,
        global: null as { name: string } | null,
        user: null as { name: string } | null,
    },

    /**
     * Laravel Echo does not expose a dedicated setter for private-channel auth headers.
     * In this app, account/user channel subscriptions can happen after auth/account changes
     * and sometimes before `connect()` is called, so we refresh the bearer token immediately
     * before each private subscription to avoid stale or null authorization.
     */
    refreshChannelAuthorizationHeader() {
        this.echo.connector.options.channelAuthorization.headers.Authorization = `Bearer ${authService.token()}`;
    },

    connect() {
        this.refreshChannelAuthorizationHeader();
        this.echo.connector.pusher.connect();

        this.bindUserChannel();
        this.bindGlobalChannel();

        const accountId = useAccountStore().contextAccount?.id;
        if (accountId) {
            this.bindAccountChannel(accountId);
        }
    },

    disconnect() {
        this.echo.disconnect();
    },

    connected() {
        axios.defaults.headers.common['X-Socket-ID'] = this.echo.socketId();
    },

    disconnected() {
        delete axios.defaults.headers.common['X-Socket-ID'];
    },

    bindGlobalChannel() {
        this.channels.global = this.echo.channel('global');

        this.$emit('global-channel-ready');
    },

    bindAccountChannel(accountId: number | string) {
        this.refreshChannelAuthorizationHeader();
        this.channels.account = this.echo.private(`account_${accountId}`);

        this.$emit('account-channel-ready');
    },

    bindUserChannel() {
        this.refreshChannelAuthorizationHeader();
        this.channels.user = this.echo.private(`user_${useUserStore().parentAuthUser.id}`);

        this.$emit('user-channel-ready');
    },

    unbindChannels() {
        this.unbindGlobalChannel();
        this.unbindAccountChannel();
        this.unbindUserChannel();
    },

    unbindGlobalChannel() {
        if (this.channels.global) {
            this.echo.leave(this.channels.global.name);
        }
    },

    unbindAccountChannel() {
        if (this.channels.account) {
            this.echo.leave(this.channels.account.name);
        }
    },

    unbindUserChannel() {
        if (this.channels.user) {
            this.echo.leave(this.channels.user.name);
        }
    },

    userChanged(newUserId: unknown, oldUserId: unknown) {
        if (newUserId == oldUserId) {
            return;
        }

        if (!newUserId) {
            this.unbindChannels();
            this.disconnect();
        } else if (!oldUserId) {
            this.connect();
        } else {
            this.unbindChannels();
            this.connect();
        }
    },

    accountChanged(newAccountId: unknown, oldAccountId: unknown) {
        if (newAccountId == oldAccountId) {
            return;
        }

        this.unbindAccountChannel();
        this.bindAccountChannel(newAccountId as number | string);
    },
};

const initializeBroadcastingConnectionCallbacks = () => {
    broadcasting.echo.connector.pusher.connection.bind('connected', () => broadcasting.connected());
    broadcasting.echo.connector.pusher.connection.bind('disconnected', () =>
        broadcasting.disconnected()
    );
};

initializeBroadcastingConnectionCallbacks();

const BroadcastingPlugin = {
    install(app: App) {
        app.config.globalProperties.$broadcasting = broadcasting;
        app.provide('broadcasting', broadcasting);
    },
};

export default BroadcastingPlugin;
